import { ThemeProvider } from '@emotion/react'
import {
  Select,
  OutlinedInput,
  Box,
  MenuItem,
  Checkbox,
  FormHelperText
} from '@mui/material'
import { forwardRef, ForwardedRef } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { useAppSelector } from '~/app/hooks'
import { currentTheme, isDarkTheme } from '~/redux/features'
import { createCustomizedTheme, MenuProps, SelectMultipleCustom } from '.'
import classNames from 'classnames/bind'
import styles from './EFSelect.module.scss'
import { EFTypography } from '../EFTypography'
import { StatusChip } from '../EFChip'
import { EFSelectMultipleProps } from './EFSelect.model'
import { EFInputLabel } from '../EFInputLabel'
import { primaryCheckboxSx } from '~/theme'

const cx = classNames.bind(styles)

export const SelectMultipleForm = forwardRef(
  (props: EFSelectMultipleProps, _ref: ForwardedRef<unknown>) => {
    const { label, data, errorMessage } = props
    const theme = useAppSelector(currentTheme)
    const isDark = useAppSelector(isDarkTheme)
    const { control } = useFormContext() ?? {}
    if (!control) return <SelectMultipleCustom {...props} />

    return (
      <Controller
        control={control}
        name={props.name}
        render={({ field }) => (
          <div className='custom-field'>
            {label && (
              <EFInputLabel
                name={props.name}
                label={label}
              />
            )}

            <ThemeProvider theme={createCustomizedTheme(isDark)}>
              <Select
                {...props}
                {...field}
                multiple
                className={cx('select-field', theme)}
                sx={{
                  '& fieldset': {
                    top: 0,
                    '& legend': { display: 'none' }
                  }
                }}
                input={
                  <OutlinedInput
                    id='select-multiple-chip'
                    label='Chip'
                  />
                }
                renderValue={selected => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value: any) => {
                      const displayName =
                        data.find(nameObj => nameObj.name === value)?.customText ?? ''
                      return (
                        <StatusChip
                          className={cx('select-chip', theme)}
                          key={value}
                          label={
                            <EFTypography fontSize='var(--fs-sm)'>
                              {displayName ?? value}
                            </EFTypography>
                          }
                        />
                      )
                    })}
                  </Box>
                )}
                value={field.value}
                labelId={field.name}
                id={field.name}
                MenuProps={MenuProps}
                onChange={event => {
                  const { value } = event.target
                  field.onChange(value) // Manually trigger the field's onChange event
                }}
              >
                {data.map(item => (
                  <MenuItem
                    className={cx('menu-select-item', theme)}
                    sx={{ paddingY: 0 }}
                    key={item.value}
                    value={item.name}
                  >
                    <Checkbox
                      size='small'
                      sx={primaryCheckboxSx}
                      checked={field.value.indexOf(item.name) > -1}
                    />
                    {item.customText}
                  </MenuItem>
                ))}
              </Select>
              {errorMessage && (
                <FormHelperText sx={{ color: 'red !important' }}>
                  {errorMessage}
                </FormHelperText>
              )}
            </ThemeProvider>
          </div>
        )}
      />
    )
  }
)
